import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

interface CoinDropdownProps {
    selectedCoin: string;
    currencyOptions: { code: string; symbol: string; image: string }[];
    onSelect: (code: string) => void;
}

const CoinDropdown: React.FC<CoinDropdownProps> = ({ selectedCoin, currencyOptions, onSelect }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const toggleCoinDropdown = () => {
        setIsDropdownOpen((prev) => !prev);
    };

    const handleCoinSelect = (code: string) => {
        onSelect(code);
        setIsDropdownOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="flex items-center space-x-3 mr-2">
            {/* <img className="hidden" src={`/currency-kas.png`} alt={`none`} />
            <img className="hidden" src={`/currency-btc.png`} alt={`none`} /> */}
            <button
                id="dropdownUsersButton"
                className="text-white bg-white/20 dark:bg-slate-800/40 hover:bg-white/30 dark:hover:bg-slate-700/60 border border-white/10 font-bold rounded-xl text-xs md:text-sm px-2 md:px-3 py-1.5 md:py-2.5 inline-flex items-center justify-between gap-2 md:gap-3 transition-all focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-900 shadow-sm"
                onClick={toggleCoinDropdown}
            >
                <div className="flex items-center gap-2">
                    <img
                        className="w-5 h-5 md:w-6 md:h-6 rounded-full shadow-sm"
                        src={
                            selectedCoin === 'BTC'
                                ? '/assets/currency-btc.png'
                                : selectedCoin === 'KAS'
                                    ? '/assets/currency-kas.png'
                                    : '/assets/currency-alph.png'
                        }
                        alt={`${selectedCoin} icon`}
                    />
                    <span className='tracking-wide hidden md:inline'>{selectedCoin}</span>
                </div>
                <FontAwesomeIcon icon={faChevronDown} className={`text-[10px] md:text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDropdownOpen && (
                <div
                    ref={dropdownRef}
                    className="fixed top-20 right-24 z-50 w-56 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                >
                    <div className="px-4 py-2 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Select Asset</span>
                    </div>
                    <ul className="py-2">
                        {currencyOptions.map((option) => (
                            <li key={option.code}>
                                <button
                                    className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-bold transition-all hover:bg-slate-50 dark:hover:bg-slate-700/50 ${selectedCoin === option.code ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20' : 'text-slate-600 dark:text-slate-300'}`}
                                    onClick={() => handleCoinSelect(option.code)}
                                >
                                    <img className="w-6 h-6 rounded-full shadow-sm" src={option.image} alt={`${option.code} icon`} />
                                    <span>{option.symbol}</span>
                                    {selectedCoin === option.code && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default CoinDropdown;
